import type React from "react"
import { NotificationItem } from "./NotificationItem"
import { useTranslation } from "react-i18next";
import { useState, useEffect, useMemo, memo } from "react";
import { useSession } from "../context/SessionContext";

interface NotificationsListProps {
  urgentTransfers: any[]
  regularNotifications: any[]
  myUsers: any[]
  activePhone: string | null
  activeNotification: any
  onNotificationClick: (
    userId: string | number,
    phone: string,
    type: string,
    urgent: boolean,
    date: Date,
    important: boolean,
    ignored: boolean,
    read: boolean,
    collaborations: any[],
    assigned_by: string,
    transferId?: number,
  ) => void
  getRelativeTime: (dateString: string) => { label: string; isStale: boolean }
  assistants: any[]
  usersCache: { [userId: string]: any }
  setUsersCache: React.Dispatch<React.SetStateAction<{ [userId: string]: any }>>
  subView: "new" | "in_process" | "agent"
}

const NotificationItemMemo = memo(NotificationItem, (prev, next) => {
  return (
    prev.notification.transferId === next.notification.transferId &&
    prev.notification.userId === next.notification.userId &&
    prev.isActive === next.isActive &&
    prev.user?.id === next.user?.id &&
    prev.user?.phone === next.user?.phone &&
    prev.user?.name === next.user?.name
  );
});

export const NotificationsList: React.FC<NotificationsListProps> = memo(({
  urgentTransfers,
  regularNotifications,
  myUsers,
  activePhone,
  activeNotification,
  onNotificationClick,
  getRelativeTime,
  assistants,
  usersCache,
  setUsersCache,
  subView
}) => {
  const { t } = useTranslation();
  const { session } = useSession();
  const [loadingUsers, setLoadingUsers] = useState<Set<string>>(new Set());

  const allNotifications = useMemo(() => 
    [...urgentTransfers, ...regularNotifications],
    [urgentTransfers, regularNotifications]
  );
  
  // Extraer todos los user_ids únicos de las notificaciones
  const userIds = useMemo(() => 
    Array.from(new Set(allNotifications.map((n) => String(n.userId)))),
    [allNotifications]
  );

  useEffect(() => {
    const fetchUsers = async () => {
      const usersToFetch = userIds.filter(
        userId => !usersCache[userId] && !loadingUsers.has(userId)
      );

      if (usersToFetch.length === 0) return;

      const newLoadingUsers = new Set(loadingUsers);
      usersToFetch.forEach(id => newLoadingUsers.add(id));
      setLoadingUsers(newLoadingUsers);
      
      const fetchPromises = usersToFetch.map(async (userId) => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/user/data/${userId}`,
            {
              method: "GET",
              headers: {
                Authorization: `${session!.token_type} ${session!.access_token}`,
              },
            }
          );
          
          if (response.ok) {
            const userData = await response.json();
            return { userId, userData };
          }
          return null;
        } catch (error) {
          console.error(`Error fetching user ${userId}:`, error);
          return null;
        }
      });

      const results = await Promise.all(fetchPromises);
      
      setUsersCache((prev) => {
        const newCache = { ...prev };
        results.forEach(result => {
          if (result) {
            newCache[result.userId] = result.userData;
          }
        });
        return newCache;
      });

      setLoadingUsers(new Set());
    };

    if (session && userIds.length > 0) {
      fetchUsers();
    }
  }, [userIds.join(','), session]);

  const hasUrgentTransfers = urgentTransfers.length > 0
  const hasRegularNotifications = regularNotifications.length > 0

  return (
    <>
      {hasUrgentTransfers && (
        <div className="flex flex-col p-2">
          <div>
            <ul>
              {urgentTransfers.map((notification: any) => {
                const user = usersCache[String(notification.userId)]
                const isActive = Boolean(
                  activeNotification && 
                  activePhone && 
                  String(activeNotification.userId) === String(notification.userId) &&
                  String(activeNotification.transferId || '') === String(notification.transferId || '') &&
                  activePhone === user?.phone
                )
                return (
                  <NotificationItemMemo
                    key={`urgent-${notification.userId}-${notification.transferId ?? "msg"}`}
                    notification={notification}
                    user={user}
                    isActive={isActive}
                    isUrgent={true}
                    onNotificationClick={onNotificationClick}
                    getRelativeTime={getRelativeTime}
                    assistants={assistants}  
                  />
                )
              })}
            </ul>
          </div>
        </div>
      )}

      {hasRegularNotifications && (
        <div className="flex flex-col p-2">
          <div>
            <ul>
              {regularNotifications.map((notification: any) => {
                const user = usersCache[String(notification.userId)]
                const isActive = Boolean(
                  activeNotification && 
                  activePhone && 
                  String(activeNotification.userId) === String(notification.userId) &&
                  String(activeNotification.transferId || '') === String(notification.transferId || '') &&
                  activePhone === user?.phone
                )
                
                
                return (
                  <NotificationItemMemo
                    key={`regular-${notification.userId}-${notification.transferId ?? "msg"}`}
                    notification={notification}
                    user={user}
                    isActive={isActive}
                    isUrgent={false}
                    onNotificationClick={onNotificationClick}
                    getRelativeTime={getRelativeTime}
                    assistants={assistants}
                  />
                )
              })}
            </ul>
          </div>
        </div>
      )}
    </>
  )
}, (prev, next) => {
  return (
    prev.urgentTransfers === next.urgentTransfers &&
    prev.regularNotifications === next.regularNotifications &&
    prev.activePhone === next.activePhone &&
    prev.activeNotification?.userId === next.activeNotification?.userId &&
    prev.activeNotification?.transferId === next.activeNotification?.transferId &&
    prev.usersCache === next.usersCache &&
    prev.subView === next.subView
  );
});
